import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../../../../hooks/useObjectEditor";
import {
	NumberInputMemo,
	NumberInput,
} from "../../../../../../../../../components/Orders";
import { StyledRow } from "../../../../../../../../../components/common";
import { EditIcon } from "../../../../../../../../../icons/edit";
import { Gray, Primary } from "../../styled";
import { OrderFormProps } from "../../../types/FormProps";

import Clickable from "./Clickable";

interface Props extends OrderFormProps {
	edit: boolean;
	setEdit: (edit: boolean) => void;
}

const OutOfTown: React.FC<Props> = ({ disabled, edit, setEdit, form, tab }) => {
	const { t } = useTranslation();

	// --- old ---
	// const formSuburban = useMemo(
	// 	() => (tab.form.suburban ?? 0) / 1000,
	// 	[tab.form.suburban],
	// );
	// const routeSuburban = useMemo(
	// 	() => tab.state.route?.suburbanPriceZones ?? 0,
	// 	[tab.state.route?.suburbanPriceZones],
	// );
	// const routeSuburbanDistance = useMemo(
	// 	() => formSuburban ?? routeSuburban ?? 0,
	// 	[formSuburban, routeSuburban],
	// );
	// const routeSuburbanDistanceRounded = useMemo(
	// 	() => Number(routeSuburbanDistance).toFixed(3),
	// 	[routeSuburbanDistance],
	// );

	/**
	 * Why these calculations:
	 *
	 * Server builds suburban from route price-zones, then applies
	 * `suburbanDistanceOverride { value, shrink }`. `value` is always an
	 * absolute target km; shrink=true replaces (set), shrink=false adjusts
	 * by add/trim (+/-) toward that target.
	 *
	 * `tab.form.suburban` = cost.distances.suburban in meters (after apply).
	 * `route.suburbanPriceZones` = geometry suburban in km (no override).
	 * Form default override 0 + shrink false is dummy — ignore for display.
	 */
	const baseSuburbanKm = useMemo(() => {
		const fromDistancesM = tab.form.suburban;
		const fromDistancesKm = Number.isFinite(fromDistancesM)
			? Number(fromDistancesM) / 1000
			: undefined;
		const fromRouteZones = tab.state.route?.suburbanPriceZones;
		const routeKm = Number.isFinite(fromRouteZones)
			? Number(fromRouteZones)
			: undefined;

		// Prefer distances (already includes override after calc), else zones.
		if (typeof fromDistancesKm === "number") return fromDistancesKm;
		if (typeof routeKm === "number") return routeKm;
		return 0;
	}, [tab.form.suburban, tab.state.route?.suburbanPriceZones]);

	const overrideKm = tab.form.suburbanDistanceOverride ?? 0;
	const overrideShrink = !!tab.form.suburbanDistanceOverrideState;
	const isDummyOverride = overrideKm === 0 && !overrideShrink;

	// After local edit, override holds the absolute target before cost refresh;
	// otherwise show base from distances / zones.
	const effectiveSuburbanKm = useMemo(() => {
		if (!isDummyOverride) return Math.max(overrideKm, 0);
		return Math.max(baseSuburbanKm, 0);
	}, [baseSuburbanKm, isDummyOverride, overrideKm]);

	const effectiveSuburbanKmRounded = useMemo(
		() => Number(effectiveSuburbanKm).toFixed(3),
		[effectiveSuburbanKm],
	);

	const [value, onChangeValue] = useState<NumberInput.Value>({
		action: "add",
		value: 0,
	});

	const valueEditor = useObjectEditor(value, onChangeValue);
	const setAction = valueEditor.useSetter("action");
	const setValue = valueEditor.useSetter("value");

	const onChange = useCallback(
		(data: NumberInput.Value) => {
			setValue(data.value);
			setAction(data.action);

			// --- old ---
			// form.setValue(
			// 	"suburbanDistanceOverrideState",
			// 	data.action === "set",
			// );
			// if (data.action === "set") {
			// 	const newOutOfTown = Math.max(data.value, 0);
			// 	form.setValue("suburbanDistanceOverride", newOutOfTown);
			// } else {
			// 	const newOutOfTown =
			// 		Number(routeSuburbanDistanceRounded) + data.value;
			// 	form.setValue("suburbanDistanceOverride", newOutOfTown);
			// }

			// UI "set"/"add" → absolute target km for the server (same as
			// OrderPage useBottomData). Example: 7.7 + add(-5.7) → value=2.
			const shrink = data.action === "set";
			const newTargetKm = shrink
				? Math.max(data.value, 0)
				: Math.max(effectiveSuburbanKm + data.value, 0);

			form.setValue("suburbanDistanceOverrideState", shrink);
			form.setValue(
				"suburbanDistanceOverride",
				Number(Number(newTargetKm).toFixed(3)),
			);

			setEdit(false);
		},
		[setValue, setAction, effectiveSuburbanKm, setEdit, form],
	);

	return (
		<StyledRow gap="6px">
			<Primary>
				{`${t([
					`orderPage.order_form.out_of_town`,
					"In particular out of town",
				])}:`}
			</Primary>

			<>
				<Gray>
					{effectiveSuburbanKmRounded} {`${t(`units.km`) ?? "km"}`}
				</Gray>

				{!disabled && (
					<Clickable onClick={() => setEdit(true)}>
						<EditIcon />
					</Clickable>
				)}

				{edit && (
					<NumberInputMemo
						shadow
						title={`${t([
							`orderPage.order_form.out_of_town`,
							"In particular out of town",
						])}:  ${effectiveSuburbanKmRounded} ${
							t(`units.km`) ?? "km"
						}`}
						unit={t(`units.km`) ?? "km"}
						value={value}
						onSubmit={onChange}
						onClose={() => setEdit(false)}
						decimalCount={3}
					/>
				)}
			</>
		</StyledRow>
	);
};

export default OutOfTown;
